import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, Trash2, Check, X, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getJobApplications, getJobs, updateApplicationStatus, deleteJobApplication } from '../../lib/api';

const AdminJobApplications = () => {
  const { t, isRTL } = useLanguage();
  const { isSuperAdmin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const loadData = async () => {
    try {
      const [appsRes, jobsRes] = await Promise.all([getJobApplications(), getJobs()]);
      setApplications(appsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error(t('خطأ في تحميل البيانات', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? t(job.title_ar, job.title_en) : t('غير محدد', 'Unknown');
  };

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await updateApplicationStatus(appId, newStatus);
      toast.success(t('تم تحديث الحالة', 'Status updated'));
      loadData();
    } catch (error) {
      toast.error(t('خطأ في تحديث الحالة', 'Error updating status'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJobApplication(selectedApp.id);
      toast.success(t('تم الحذف', 'Deleted'));
      setDeleteDialogOpen(false);
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(t('خطأ في الحذف', 'Error deleting'));
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-500',
    reviewed: 'bg-blue-500/20 text-blue-500',
    accepted: 'bg-green-500/20 text-green-500',
    rejected: 'bg-red-500/20 text-red-500'
  };

  const statusLabels = {
    pending: { ar: 'قيد المراجعة', en: 'Pending' },
    reviewed: { ar: 'تمت المراجعة', en: 'Reviewed' },
    accepted: { ar: 'مقبول', en: 'Accepted' },
    rejected: { ar: 'مرفوض', en: 'Rejected' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-job-applications">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('طلبات التوظيف', 'Job Applications')}</h1>
          <p className="text-gray-400">{t('إدارة طلبات التوظيف الواردة', 'Manage incoming job applications')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <Card className="bg-raad-card border-white/10">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{applications.filter(a => a.status === 'pending').length}</p>
            <p className="text-gray-500 text-sm">{t('قيد المراجعة', 'Pending')}</p>
          </CardContent>
        </Card>
        <Card className="bg-raad-card border-white/10">
          <CardContent className="p-4 text-center">
            <Eye className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{applications.filter(a => a.status === 'reviewed').length}</p>
            <p className="text-gray-500 text-sm">{t('تمت المراجعة', 'Reviewed')}</p>
          </CardContent>
        </Card>
        <Card className="bg-raad-card border-white/10">
          <CardContent className="p-4 text-center">
            <Check className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{applications.filter(a => a.status === 'accepted').length}</p>
            <p className="text-gray-500 text-sm">{t('مقبول', 'Accepted')}</p>
          </CardContent>
        </Card>
        <Card className="bg-raad-card border-white/10">
          <CardContent className="p-4 text-center">
            <X className="w-6 h-6 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{applications.filter(a => a.status === 'rejected').length}</p>
            <p className="text-gray-500 text-sm">{t('مرفوض', 'Rejected')}</p>
          </CardContent>
        </Card>
      </div>

      {applications.length === 0 ? (
        <Card className="bg-raad-card border-white/10">
          <CardContent className="py-12 text-center">
            <p className="text-gray-400">{t('لا توجد طلبات توظيف', 'No job applications yet')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <Card key={app.id} className="bg-raad-card border-white/10 hover:border-white/30 transition-all cursor-pointer" onClick={() => { setSelectedApp(app); setDialogOpen(true); }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                      <span className="text-raad-gold font-semibold">{app.name[0]?.toUpperCase()}</span>
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{app.name}</h3>
                      <p className="text-raad-gold text-sm">{getJobTitle(app.job_id)}</p>
                      <p className="text-gray-500 text-xs">
                        {app.email} • <span className="phone-ltr inline-block" dir="ltr">{app.phone}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-sm">
                      {new Date(app.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs ${statusColors[app.status]}`}>
                      {t(statusLabels[app.status].ar, statusLabels[app.status].en)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-raad-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{t('تفاصيل الطلب', 'Application Details')}</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{t('الاسم', 'Name')}:</span>
                  <p className="text-white">{selectedApp.name}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('البريد', 'Email')}:</span>
                  <p className="text-white">{selectedApp.email}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('الهاتف', 'Phone')}:</span>
                  <p className="text-white phone-ltr" dir="ltr">{selectedApp.phone}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('التاريخ', 'Date')}:</span>
                  <p className="text-white">{new Date(selectedApp.created_at).toLocaleString(isRTL ? 'ar-SA' : 'en-US')}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">{t('الوظيفة', 'Job')}:</span>
                  <p className="text-raad-gold">{getJobTitle(selectedApp.job_id)}</p>
                </div>
              </div>

              {selectedApp.cover_letter && (
                <div>
                  <span className="text-gray-500 text-sm">{t('رسالة التقديم', 'Cover Letter')}:</span>
                  <p className="text-white mt-2 p-4 rounded-lg bg-white/5 whitespace-pre-wrap">{selectedApp.cover_letter}</p>
                </div>
              )}

              {selectedApp.resume_url && (
                <div>
                  <a href={selectedApp.resume_url} target="_blank" rel="noopener noreferrer" className="text-raad-gold hover:underline">
                    {t('عرض السيرة الذاتية', 'View Resume')}
                  </a>
                </div>
              )}

              <div>
                <span className="text-gray-500 text-sm">{t('تغيير الحالة', 'Change Status')}:</span>
                <Select value={selectedApp.status} onValueChange={(v) => handleStatusChange(selectedApp.id, v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-raad-card border-white/10">
                    <SelectItem value="pending">{t('قيد المراجعة', 'Pending')}</SelectItem>
                    <SelectItem value="reviewed">{t('تمت المراجعة', 'Reviewed')}</SelectItem>
                    <SelectItem value="accepted">{t('مقبول', 'Accepted')}</SelectItem>
                    <SelectItem value="rejected">{t('مرفوض', 'Rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isSuperAdmin && (
                <div className="flex justify-end pt-4">
                  <Button variant="ghost" onClick={() => setDeleteDialogOpen(true)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4 me-2" />
                    {t('حذف', 'Delete')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-raad-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('تأكيد الحذف', 'Confirm Delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">{t('هل أنت متأكد من حذف هذا الطلب؟', 'Are you sure you want to delete this application?')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">{t('إلغاء', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">{t('حذف', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminJobApplications;
